import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import Button from '@ids-ts/button';
import '../styles/RegistrationSuccess.css';
import { isChallengeWindowOpen } from '../config';
const CONTENT = {
    title: 'Congrats!',
    school: {
        messagePrefix: "You've successfully registered for the Intuit Hour of Finance Challenge as part of ",
        messageSuffix: '',
    },
    community: {
        messagePrefix: "You've successfully registered as part of ",
        messageSuffix: ' for the Intuit Hour of Finance Challenge.',
    },
    independent: {
        messagePrefix: '',
        messageSuffix: ", you've successfully registered for the Intuit Hour of Finance Challenge.",
    },
    joinCodeLabel: 'Join Code',
    joinCodeDescription: 'Your students will access the game using the Join code anytime between February 23rd, 2026 and April 15th, 2026',
    joinCodeDescriptionIndependent: 'You will access the game using this Join code.',
    impactTrackerCodeLabel: 'Impact Tracker Code',
    impactTrackerCodeDescription: "Use this code on the Impact Tracker to see your school's progress",
    doneButtonText: 'Done',
    copiedFeedback: 'Copied!',
};
const INFO_BOX = {
    questionText: "Don't see an email?",
    bodyText: 'Check your spam folder for your confirmation email. Email ',
    emailAddress: 'education@intuit.com',
    bodySuffix: ' for additional assistance.',
};
const formatCode = (code) => {
    if (!code)
        return '';
    const cleaned = code.replace(/\s/g, '');
    return cleaned.replace(/(.{3})/g, '$1 ').trim();
};
const CopyIcon = () => (_jsxs("svg", { width: "36", height: "36", viewBox: "0 0 36 36", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("path", { d: "M24 3H10.5C9.67157 3 9 3.67157 9 4.5V7.5H7.5C6.67157 7.5 6 8.17157 6 9V31.5C6 32.3284 6.67157 33 7.5 33H21C21.8284 33 22.5 32.3284 22.5 31.5V28.5H24C24.8284 28.5 25.5 27.8284 25.5 27V24H28.5C29.3284 24 30 23.3284 30 22.5V4.5C30 3.67157 29.3284 3 28.5 3H24Z", stroke: "currentColor", strokeWidth: "2", fill: "none" }), _jsx("path", { d: "M9 10.5V9C9 8.17157 9.67157 7.5 10.5 7.5H24V27C24 27.8284 24.6716 28.5 25.5 28.5H27V22.5C27 23.3284 26.3284 24 25.5 24H22.5V31.5", stroke: "currentColor", strokeWidth: "2", fill: "none" })] }));
export const RegistrationSuccess = ({ registrationType, joinCode, schoolName, parentOrg, otherParentOrg, fullName, onDone, onViewChallenge, showSurveyButton = true, showViewChallengeButton = true, forceShowCode = false, }) => {
    const [copiedCode, setCopiedCode] = useState(null);
    const handleCopyToClipboard = async (text, codeType) => {
        if (!text)
            return;
        try {
            await navigator.clipboard.writeText(text.replace(/\s/g, ''));
            setCopiedCode(codeType);
            setTimeout(() => setCopiedCode(null), 2000);
        }
        catch (err) {
            // Silently fail
        }
    };
    const renderSuccessMessage = () => {
        if (registrationType === 'independent') {
            return (_jsxs("p", { className: "reg-success-message", children: [CONTENT.independent.messagePrefix, _jsx("span", { className: "reg-success-school-name", children: fullName }), CONTENT.independent.messageSuffix] }));
        }
        if (registrationType === 'school') {
            return (_jsxs("p", { className: "reg-success-message", children: [CONTENT.school.messagePrefix, _jsx("span", { className: "reg-success-school-name", children: schoolName }), CONTENT.school.messageSuffix] }));
        }
        if (registrationType === 'community') {
            // Map database values to display names
            const parentOrgMap = {
                '4-H': '4-H',
                'girlScouts': 'Girl Scouts',
                'boyScouts': 'Boy Scouts',
            };
            // Use otherParentOrg if parentOrg is "other", otherwise map parentOrg
            let displayName;
            if (parentOrg === 'other') {
                displayName = otherParentOrg;
            }
            else if (parentOrg) {
                displayName = parentOrgMap[parentOrg] || parentOrg;
            }
            return (_jsxs("p", { className: "reg-success-message", children: [CONTENT.community.messagePrefix, _jsx("span", { className: "reg-success-school-name", children: displayName }), CONTENT.community.messageSuffix] }));
        }
        return null;
    };
    // Internal CS reps / Intuit employees always see the codes directly regardless of
    // whether the challenge window is open.
    const showCodes = isChallengeWindowOpen() || forceShowCode;
    const showViewChallengeBtn = isChallengeWindowOpen() && showViewChallengeButton;
    return (_jsx("div", { className: "reg-success-container", children: _jsxs("div", { className: "reg-success-content", children: [renderSuccessMessage(), !showCodes && (_jsx("div", { className: "reg-success-codes-container", children: _jsx("p", { className: "reg-success-email-notice", children: "Please check your email for registration confirmation. You will also receive your join code and a link for your students at the same email address when the Challenge window opens on February 23rd, 2026." }) })), showCodes && (_jsx("div", { className: "reg-success-codes-container", children: _jsxs("div", { className: "reg-success-code-section", children: [_jsx("p", { className: "reg-success-code-label", children: CONTENT.joinCodeLabel }), _jsxs("div", { className: "reg-success-code-card", children: [_jsxs("button", { type: "button", className: "reg-success-code-button", onClick: () => handleCopyToClipboard(joinCode, 'join'), "aria-label": "Copy Join Code", children: [_jsx("span", { className: "reg-success-code-text", children: formatCode(joinCode) }), _jsx("div", { className: "reg-success-copy-icon", children: _jsx(CopyIcon, {}) })] }), copiedCode === 'join' && (_jsx("span", { className: "reg-success-copied-feedback", children: CONTENT.copiedFeedback }))] }), _jsx("p", { className: "reg-success-code-description", children: registrationType === 'independent'
                                    ? CONTENT.joinCodeDescriptionIndependent
                                    : CONTENT.joinCodeDescription })] }) })), showSurveyButton && onDone && (_jsxs("div", { className: "reg-success-button-container", children: [_jsx(Button, { className: "reg-success-done-button", onClick: onDone, children: CONTENT.doneButtonText }), showViewChallengeBtn && onViewChallenge && (_jsx(Button, { className: "reg-success-view-challenge-button", onClick: onViewChallenge, children: "View Student Challenge" }))] })), _jsx("div", { className: "reg-success-info-box", children: _jsxs("div", { className: "reg-success-info-content", children: [_jsx("div", { className: "reg-success-info-question", children: INFO_BOX.questionText }), _jsxs("div", { className: "reg-success-info-contact", children: [INFO_BOX.bodyText, _jsx("a", { href: `mailto:${INFO_BOX.emailAddress}`, className: "reg-success-info-link", children: INFO_BOX.emailAddress }), INFO_BOX.bodySuffix] })] }) })] }) }));
};
